import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { TagPicker, type TagSelection } from "@/components/tags/TagPicker"
import { ChannelIcon } from "@/lib/channel-icons"
import { useCmdEnterSubmit } from "@/lib/use-cmd-enter-submit"
import type { ChannelOption, PageProps } from "@/types/inertia"

const EMPTY_TAGS: TagSelection = { tagIds: [], newTags: [] }

// Global "New piece" flow: (1) pick a channel, (2) give the piece a short
// descriptive name and optional tags. POSTs to /pieces — the piece lands in
// `pending`, and the server redirects to its detail page so the operator can
// fill in its content. Each call site renders its own modal with its own
// trigger; channel + tag options come from the `new_piece_options` shared
// prop.
export function NewPieceModal({ trigger }: { trigger: React.ReactNode }) {
  const { props } = usePage<PageProps>()
  const channels = props.new_piece_options?.channels ?? []
  const availableTags = props.new_piece_options?.tags ?? []

  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<ChannelOption | null>(null)
  const [name, setName] = React.useState("")
  const [tags, setTags] = React.useState<TagSelection>(EMPTY_TAGS)
  const [submitting, setSubmitting] = React.useState(false)

  const reset = () => {
    setSelected(null)
    setName("")
    setTags(EMPTY_TAGS)
    setSubmitting(false)
  }

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) reset()
  }

  const submit = () => {
    if (!selected || submitting) return
    setSubmitting(true)
    router.post(
      "/pieces",
      {
        channel: selected.slug,
        name: name.trim(),
        tag_ids: tags.tagIds,
        new_tags: tags.newTags,
      },
      {
        preserveScroll: false,
        // Server redirects to the new piece's detail page. Close the
        // dialog explicitly on success — Inertia's page swap doesn't
        // unmount the dialog's portal reliably, so the modal would
        // otherwise stick around on the new page.
        onSuccess: () => {
          setOpen(false)
          reset()
        },
        onError: () => setSubmitting(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        {selected === null ? (
          <PickChannelStep channels={channels} onPick={setSelected} />
        ) : (
          <DetailsStep
            selected={selected}
            name={name}
            onNameChange={setName}
            availableTags={availableTags}
            tags={tags}
            onTagsChange={setTags}
            onBack={() => setSelected(null)}
            onSubmit={submit}
            submitting={submitting}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PickChannelStep({
  channels,
  onPick,
}: {
  channels: ChannelOption[]
  onPick: (c: ChannelOption) => void
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>New piece</DialogTitle>
      </DialogHeader>
      <p className="text-xs text-ink-muted">
        Pick a channel for the new piece. It starts in Pending — fill in its
        content, then queue or schedule it.
      </p>
      <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {channels.map((c) => (
          <button
            key={c.slug}
            type="button"
            onClick={() => onPick(c)}
            className="flex items-center gap-2 rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body hover:border-accent hover:text-accent-display"
          >
            <ChannelIcon channelType={c.channel_type} color={c.color} className="h-3.5 w-3.5" />
            {c.name}
          </button>
        ))}
      </div>
    </>
  )
}

function DetailsStep({
  selected,
  name,
  onNameChange,
  availableTags,
  tags,
  onTagsChange,
  onBack,
  onSubmit,
  submitting,
}: {
  selected: ChannelOption
  name: string
  onNameChange: (v: string) => void
  availableTags: Array<{ id: number; name: string; color: string; icon: string }>
  tags: TagSelection
  onTagsChange: (v: TagSelection) => void
  onBack: () => void
  onSubmit: () => void
  submitting: boolean
}) {
  const onKeyDownCapture = useCmdEnterSubmit<HTMLFormElement>(onSubmit)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
      onKeyDownCapture={onKeyDownCapture}
    >
      <DialogHeader>
        <DialogTitle>Name this piece</DialogTitle>
      </DialogHeader>
      <div className="mt-2 space-y-3">
        <div className="flex items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-2 text-sm">
          <ChannelIcon channelType={selected.channel_type} color={selected.color} className="h-3.5 w-3.5" />
          <span className="text-ink-display">{selected.name}</span>
          <button
            type="button"
            onClick={onBack}
            className="ml-auto inline-flex items-center gap-0.5 text-xs text-ink-muted hover:text-ink-display"
          >
            <ChevronLeft className="h-3 w-3" />
            Change
          </button>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="new-piece-name">Name</label>
          <Input
            id="new-piece-name"
            autoFocus
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. Contrarian tweet"
          />
          <p className="text-xs text-ink-muted">
            Internal only — how this piece shows up on cards and todos.
          </p>
        </div>
        <div className="space-y-1.5">
          <label>Tags</label>
          <TagPicker available={availableTags} value={tags} onChange={onTagsChange} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
          <Button type="submit" disabled={submitting}>
            Create piece
          </Button>
        </div>
      </div>
    </form>
  )
}
